import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import {
  FiUsers,
  FiSearch,
  FiX,
  FiCalendar,
  FiDownload,
  FiRefreshCw,
  FiMapPin,
  FiStar,
  FiCheckCircle,
  FiSlash,
  FiPauseCircle,
} from "react-icons/fi";
import axiosInstance from "../config/axiosInstance";
import EmptyState from "../components/EmptyState";
import NoData from "../components/NoData";
import Pagination from "../components/Pagination";
import { useAuth } from "../contexts/AuthContext";
import { formatDate, timeAgo, onlyDigits } from "../utils/format";
import { downloadSpreadsheet } from "../utils/download";

const ITEMS_PER_PAGE = 20;

const STATUS_TABS = [
  { status: "", key: "TODOS", label: "Todos" },
  { status: "ATIVO", key: "ATIVO", label: "Ativos" },
  { status: "INATIVO", key: "INATIVO", label: "Inativos" },
  { status: "SUSPENSO", key: "SUSPENSO", label: "Suspensos" },
];

const STATUS_META = {
  ATIVO: { label: "Ativo", tone: "success", icon: FiCheckCircle },
  INATIVO: { label: "Inativo", tone: "info", icon: FiPauseCircle },
  SUSPENSO: { label: "Suspenso", tone: "danger", icon: FiSlash },
};

const initialsOf = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

// O CPF aparece parcial: para conferir um cadastro bastam os extremos, e a
// listagem varre a base inteira. O número completo sai só no Excel, que é uma
// ação deliberada da equipe.
const maskCpf = (value) => {
  const digits = onlyDigits(value);
  if (digits.length !== 11) return value || "";
  return `${digits.slice(0, 3)}.***.***-${digits.slice(9)}`;
};

const formatPhone = (value) => {
  const digits = onlyDigits(value);
  if (digits.length === 11)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  if (digits.length === 10)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return value || "";
};

const originOf = (customer) =>
  customer?.company?.nomeFantasia ||
  customer?.company?.razaoSocial ||
  "Site Shopalli";

const startOfDay = (date) =>
  date ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0) : null;

const endOfDay = (date) =>
  date ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59) : null;

const AdminCustomersPage = () => {
  const { user } = useAuth();

  const [customers, setCustomers] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const datePickerRef = useRef(null);

  const isStaff = ["ADMIN", "POWER_USER"].includes(user?.role);

  // A busca vai para o banco, então espera o usuário parar de digitar.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filterParams = useMemo(() => {
    const from = startOfDay(dateRange.from);
    const to = endOfDay(dateRange.to) || endOfDay(dateRange.from);

    return {
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(debouncedSearch ? { q: debouncedSearch } : {}),
      ...(from ? { createdFrom: from.toISOString() } : {}),
      ...(to ? { createdTo: to.toISOString() } : {}),
    };
  }, [statusFilter, debouncedSearch, dateRange.from, dateRange.to]);

  const fetchCustomers = useCallback(
    async (page = 1) => {
      if (!isStaff) return;

      setLoading(true);
      try {
        const response = await axiosInstance.get("/customers", {
          params: { page, itemsPerPage: ITEMS_PER_PAGE, ...filterParams },
        });
        const payload = response.data?.data || {};
        setCustomers(payload.data || []);
        setCounts(payload.counts || {});
        setTotalPages(payload.totalPages || 1);
        setTotalCount(payload.totalCount || 0);
      } catch (error) {
        setCustomers([]);
        setCounts({});
        setTotalPages(1);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [isStaff, filterParams]
  );

  // Trocar de filtro volta para a primeira página; o fetch acompanha a página.
  useEffect(() => {
    setCurrentPage(1);
  }, [filterParams]);

  useEffect(() => {
    fetchCustomers(currentPage);
  }, [fetchCustomers, currentPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const dateRangeLabel = useMemo(() => {
    if (dateRange.from && dateRange.to) {
      return `${dateRange.from.toLocaleDateString("pt-BR")} - ${dateRange.to.toLocaleDateString("pt-BR")}`;
    }
    if (dateRange.from) return `De ${dateRange.from.toLocaleDateString("pt-BR")}`;
    return "Selecionar intervalo";
  }, [dateRange.from, dateRange.to]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadSpreadsheet("/customers/export", filterParams, "clientes");
    } catch (error) {
      window.alert("Não foi possível exportar os clientes. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  const activeChips = [
    debouncedSearch && {
      key: "search",
      label: `Busca: "${debouncedSearch}"`,
      clear: () => setSearchTerm(""),
    },
    (dateRange.from || dateRange.to) && {
      key: "date",
      label: `Cadastro: ${dateRangeLabel}`,
      clear: () => setDateRange({ from: undefined, to: undefined }),
    },
  ].filter(Boolean);

  const clearAllFilters = () => {
    setSearchTerm("");
    setDateRange({ from: undefined, to: undefined });
  };

  if (!isStaff) {
    return (
      <EmptyState
        icon={FiUsers}
        title="Acesso restrito"
        description="A listagem de clientes está disponível apenas para usuários POWER_USER ou ADMIN."
      />
    );
  }

  const renderStatus = (customer) => {
    const meta = STATUS_META[customer?.status];
    if (!meta) return <span className="muted">-</span>;
    const Icon = meta.icon;

    return (
      <span
        className="badge"
        data-tone={meta.tone}
        style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
      >
        <Icon size={12} />
        {meta.label}
      </span>
    );
  };

  return (
    <div className="fade-in page-stack">
      <div className="page-header">
        <div>
          <h2>Clientes</h2>
          <p>
            Todos os compradores cadastrados na plataforma, com o canal de
            origem do cadastro e o último acesso de cada um.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => fetchCustomers(currentPage)}
            disabled={loading}
            style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
          >
            <FiRefreshCw size={16} />
            Atualizar
          </button>
          <button
            type="button"
            className="button button--primary"
            onClick={handleExport}
            disabled={exporting}
            style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
          >
            <FiDownload size={16} />
            {exporting ? "Gerando Excel..." : "Exportar Excel"}
          </button>
        </div>
      </div>

      <div className="section-card">
        <div className="plist-toolbar">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`plist-tab${statusFilter === tab.status ? " is-active" : ""}`}
              onClick={() => setStatusFilter(tab.status)}
            >
              {tab.label}
              <span className="plist-tab__count">{counts[tab.key] || 0}</span>
            </button>
          ))}
        </div>

        <div className="plist-filters">
          <div className="field" style={{ margin: 0 }}>
            <label className="label" htmlFor="customers-search">
              Buscar
            </label>
            <div className="plist-search">
              <FiSearch className="plist-search__icon" size={16} />
              <input
                id="customers-search"
                className="input"
                type="text"
                placeholder="Nome, e-mail, CPF ou telefone"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="plist-search__clear"
                  onClick={() => setSearchTerm("")}
                  aria-label="Limpar busca"
                >
                  <FiX size={13} />
                </button>
              )}
            </div>
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label className="label">Período de cadastro</label>
            <div className="plist-daterange" ref={datePickerRef}>
              <button
                type="button"
                className="input"
                onClick={() => setIsDatePickerOpen((prev) => !prev)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
              >
                <span>{dateRangeLabel}</span>
                <FiCalendar />
              </button>

              {isDatePickerOpen && (
                <div className="plist-daterange__popover">
                  <DayPicker
                    mode="range"
                    selected={dateRange}
                    onSelect={(range) =>
                      setDateRange(range || { from: undefined, to: undefined })
                    }
                    numberOfMonths={1}
                  />
                  <button
                    type="button"
                    className="button button--ghost"
                    style={{ width: "100%", marginTop: "8px" }}
                    onClick={() => {
                      setDateRange({ from: undefined, to: undefined });
                      setIsDatePickerOpen(false);
                    }}
                  >
                    Limpar período
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {activeChips.length > 0 && (
          <div className="plist-chips">
            <span>Filtros:</span>
            {activeChips.map((chip) => (
              <span key={chip.key} className="plist-chip">
                {chip.label}
                <button
                  type="button"
                  onClick={chip.clear}
                  aria-label={`Remover ${chip.label}`}
                >
                  <FiX size={12} />
                </button>
              </span>
            ))}
            <button type="button" className="plist-chips__reset" onClick={clearAllFilters}>
              Limpar tudo
            </button>
          </div>
        )}

        <div className="muted" style={{ marginBottom: "0.75rem", fontSize: "0.84rem" }}>
          {loading
            ? "Carregando clientes..."
            : `Exibindo ${customers.length} de ${totalCount} cliente(s)`}
        </div>

        {loading ? (
          <div className="plist-scroll">
            <table className="plist-table">
              <tbody>
                {Array.from({ length: 6 }).map((_, index) => (
                  <tr key={index}>
                    <td>
                      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                        <div
                          className="plist-skeleton"
                          style={{ width: 38, height: 38, borderRadius: "50%" }}
                        />
                        <div style={{ flex: 1 }}>
                          <div className="plist-skeleton" style={{ width: "58%" }} />
                          <div
                            className="plist-skeleton"
                            style={{ width: "32%", height: 10, marginTop: 8 }}
                          />
                        </div>
                      </div>
                    </td>
                    <td><div className="plist-skeleton" style={{ width: "72%" }} /></td>
                    <td><div className="plist-skeleton" style={{ width: 110 }} /></td>
                    <td><div className="plist-skeleton" style={{ width: 90 }} /></td>
                    <td><div className="plist-skeleton" style={{ width: 100 }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : customers.length === 0 ? (
          <NoData
            title={
              activeChips.length > 0 || statusFilter
                ? "Nenhum resultado para os filtros"
                : "Nenhum cliente cadastrado"
            }
            description={
              activeChips.length > 0 || statusFilter
                ? "Ajuste a busca, o status ou o período selecionado."
                : "Os clientes aparecem aqui conforme se cadastram na plataforma."
            }
          />
        ) : (
          <>
            <div className="adminlist-table-wrap">
              <div className="plist-scroll">
                <table className="plist-table">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Contato</th>
                      <th>Origem</th>
                      <th>Status</th>
                      <th>Último acesso</th>
                      <th>Cadastro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer) => {
                      const city = customer?.addressCity;

                      return (
                        <tr key={customer.id}>
                          <td>
                            <div className="plist-seller">
                              <span className="plist-avatar plist-avatar--lg">
                                {initialsOf(customer?.name)}
                              </span>
                              <div>
                                <div className="plist-seller__name">
                                  {customer?.name || "-"}
                                </div>
                                <div className="plist-product__meta">
                                  {customer?.cpf && (
                                    <span className="plist-tag">
                                      {maskCpf(customer.cpf)}
                                    </span>
                                  )}
                                  {customer?.isVip && (
                                    <span
                                      className="plist-tag"
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 4,
                                      }}
                                    >
                                      <FiStar size={11} />
                                      VIP
                                    </span>
                                  )}
                                </div>
                                {city && (
                                  <div
                                    className="muted"
                                    style={{
                                      fontSize: "0.74rem",
                                      marginTop: 3,
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 4,
                                    }}
                                  >
                                    <FiMapPin size={11} />
                                    {city}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>

                          <td>
                            <span className="plist-contact">
                              {customer?.email || "-"}
                              {customer?.phoneNumber && (
                                <span>{formatPhone(customer.phoneNumber)}</span>
                              )}
                            </span>
                          </td>

                          <td>
                            <span className="plist-tag">{originOf(customer)}</span>
                          </td>

                          <td>{renderStatus(customer)}</td>

                          <td>
                            {customer?.lastLoginAt ? (
                              <div
                                className="plist-date"
                                title={formatDate(customer.lastLoginAt)}
                              >
                                {timeAgo(customer.lastLoginAt)}
                                <span>{formatDate(customer.lastLoginAt)}</span>
                              </div>
                            ) : (
                              <span className="muted">Nunca acessou</span>
                            )}
                          </td>

                          <td>
                            <div
                              className="plist-date"
                              title={formatDate(customer?.createdAt)}
                            >
                              {customer?.createdAt ? timeAgo(customer.createdAt) : "-"}
                              <span>{formatDate(customer?.createdAt)}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="adminlist-mobile-list">
              {customers.map((customer) => (
                <div key={customer.id} className="adminlist-card">
                  <div className="adminlist-card__header">
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <span className="plist-avatar plist-avatar--lg">
                        {initialsOf(customer?.name)}
                      </span>
                      <div>
                        <div className="adminlist-card__name">
                          {customer?.name || "-"}
                        </div>
                        <div className="adminlist-card__sub">
                          {customer?.cpf ? maskCpf(customer.cpf) : originOf(customer)}
                        </div>
                      </div>
                    </div>
                    {renderStatus(customer)}
                  </div>

                  <div className="adminlist-card__meta">
                    <span className="adminlist-card__meta-item">
                      {customer?.email || "-"}
                    </span>
                    {customer?.phoneNumber && (
                      <span className="adminlist-card__meta-item">
                        {formatPhone(customer.phoneNumber)}
                      </span>
                    )}
                    {customer?.addressCity && (
                      <span className="adminlist-card__meta-item">
                        <FiMapPin size={12} />
                        {customer.addressCity}
                      </span>
                    )}
                  </div>

                  <div className="adminlist-card__footer">
                    <div className="adminlist-card__date">
                      <span>Cadastro: {formatDate(customer?.createdAt)}</span>
                      <span style={{ display: "block" }}>
                        {customer?.lastLoginAt
                          ? `Último acesso ${timeAgo(customer.lastLoginAt)}`
                          : "Nunca acessou"}
                      </span>
                    </div>
                    <span className="plist-tag">{originOf(customer)}</span>
                  </div>
                </div>
              ))}
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AdminCustomersPage;
